import React from "react";
import { m } from "kremling";
import { isEmpty, isNumber, isBoolean } from "lodash";
import { EmptyCell } from "../cells";
import styles from "./table-body.styles.css";
import { ColumnId, TableSchema } from "../types";
import { Selection } from "../hooks/use-table-selection.hook";
import {
  calculateRightStickyPosition,
  isColumnBeforeRightSticky,
} from "../cp-data-table.helper";

function Cell({
  columnId,
  aggregatedWidth,
  resource,
  schema,
  rowIsDisabled,
  columnWidth,
  selection,
  resourceIndex,
  onSelect,
  orderedColumns,
  columnWidths,
}: {
  columnId: ColumnId;
  aggregatedWidth: number | null;
  resource: any;
  schema: TableSchema;
  rowIsDisabled?: boolean;
  columnWidth: number;
  selection: Selection | null;
  resourceIndex: number;
  onSelect:
    | ((evt: React.MouseEvent, resource: any, resourceIndex: number) => void)
    | null;
  orderedColumns: ColumnId[];
  columnWidths: Record<string, number | string>;
}) {
  const columnSchema = schema[columnId];
  if (!columnSchema) return null;

  const CellComponent = columnSchema.cell.component;
  const value = columnSchema.fieldId ? resource[columnSchema.fieldId] : null;
  const formattedValue = columnSchema.cell.formatValue
    ? columnSchema.cell.formatValue(value)
    : value;
  const cellIsEmpty =
    isNumber(formattedValue) || isBoolean(formattedValue)
      ? false
      : isEmpty(formattedValue);
  const showEmpty =
    cellIsEmpty &&
    !CellComponent.overrideEmptyCell &&
    columnSchema.dataType !== "component";

  const schemaProps = columnSchema.cell.props;
  const props =
    typeof schemaProps === "function"
      ? schemaProps({ resource, value })
      : schemaProps;

  function renderCell() {
    if (showEmpty) {
      return <EmptyCell isDisabled={rowIsDisabled} />;
    }
    return (
      <CellComponent
        isDisabled={rowIsDisabled}
        resource={resource}
        resourceIndex={resourceIndex}
        columnId={columnId}
        columnSchema={columnSchema}
        value={formattedValue}
        selection={selection}
        onSelect={onSelect}
        {...props}
      />
    );
  }

  const isRightSticky = columnSchema.sticky === "right";
  const isLeftSticky =
    columnSchema.sticky === "left" || columnSchema.sticky === true;
  const isBeforeRightSticky = isColumnBeforeRightSticky(
    columnId,
    orderedColumns,
    schema,
  );

  let stickyStyle = {};
  if (isLeftSticky && aggregatedWidth !== null) {
    stickyStyle = {
      left: `${(aggregatedWidth - columnWidth) / 10}rem`,
    };
  } else if (isRightSticky) {
    const rightOffset = calculateRightStickyPosition(
      columnId,
      orderedColumns,
      schema,
      columnWidths,
    );
    stickyStyle = {
      right: `${rightOffset / 10}rem`,
    };
  }

  return (
    <td
      className={m(styles.stickyCell, !!columnSchema.sticky)
        .m(styles.rightStickyCell, isRightSticky)
        .m(`cp-text-${props?.align}`, props?.align)
        .m("cp-ellipsis", columnSchema.dataType !== "component")
        .m(styles.forceVerticalBorder, columnSchema.showVerticalBorder === true)
        .m(
          styles.forceNoVerticalBorder,
          columnSchema.showVerticalBorder === false || isBeforeRightSticky,
        )}
      style={stickyStyle}
    >
      {renderCell()}
    </td>
  );
}
export const MemoCell = React.memo(Cell);
